/**
 * AppointmentDetail — the appointment record as a DEDICATED PAGE (route
 * `appointment.detail?id=…`), replacing the side drawer.
 *
 * 2026-08-06, operator: "die appointment detail sideview ist nicht gut, in
 * calenso ist es eine dedizierte seite" + a Calenso screenshot. The information
 * architecture is modelled on that reference:
 *
 *   · a breadcrumb (Dashboard › Appointments › <customer>) and an ACTION BAR
 *     across the top — the lifecycle verbs, not a kebab;
 *   · a wide left column of record cards — Basic information (incl. the answered
 *     booking questions), Participants, Date & time, Price & payment, Notes;
 *   · a narrow right column, "Appointment metadata" — customer, created,
 *     booking channel, the UTM parameters each with its explaining caption, the
 *     booking client (source URL, IP, browser, OS, engine, device, user agent)
 *     and the customer/admin deep links with copy buttons.
 *
 * WHERE IT DELIBERATELY DIFFERS FROM THE REFERENCE: Calenso's page is an edit
 * form with Save / Discard. There is no `updateAppointment` operation — only
 * reschedule, cancel, check-in and no-show — so this page READS the record and
 * acts through the ops that exist. Fields are values, not inputs that would
 * silently drop what you typed. The gap is filed as REQ-132.
 */
import { useCallback, useEffect, useState, type CSSProperties, type ReactNode } from "react";
import { Button, Dropdown, Icon, Input, T, Tooltip } from "@v0y4ge/jrni-ui";
import { useT } from "@v0y4ge/i18n-runtime";
import { Screen, PageHeader } from "../../shell/chrome";
import { Modal } from "../../shell/Overlay";
import { useNav, routeToPath } from "../../shell/router";
import { LoadState } from "../../shell/composites";
import { useIsMobile } from "../../shell/useResponsive";
import { toast } from "../../shell/toast";
import { can } from "../../shell/access";
import {
  addBlock, addAppointmentPayment, cancelAppointment, checkInAppointment, deleteAppointmentPayment,
  getAppointment, listBlocks, markAppointmentNoShow, removeBlock, setAppointmentPrice, updateAppointmentPayment,
  type Appointment, type ApptStatus, type BlockEntry, type BlockKind,
} from "./appointments-api";
import { RescheduleDrawer } from "./AppointmentsBook";
import { TimeZoneNote, zoneLabel } from "../../shell/TimeZoneNote";
import { tzContext, wallClockToIso, friendlyTime } from "../../lib/datetime";
import { useStaff } from "../staff/useStaff";
import { useLocations } from "../locations/useLocations";

/* Whose wall clock is this? (2026-08-07, Pat)
 * A booking is STORED as the branch's local date + start/end. The assigned
 * staff member may sit in another zone — that is exactly what the profile
 * default on their record is for — so the record states the zone it is
 * printing and, when the two differ, the same slot in the staff member's own
 * zone. Nothing is silently re-zoned: the big fields keep branch time, which
 * is what the branch, the customer and the room are all on. */
function ApptZoneLine({ appt }: { appt: { date: string; start: string; end: string; locationId: string; staffId: string | null; staffName: string | null } }) {
  const t = useT();
  const { members } = useStaff();
  const { locations } = useLocations();
  const branchTz = locations.find((l) => l.id === appt.locationId)?.tz || undefined;
  const ctx = tzContext(branchTz);
  const staff = members.find((m) => (appt.staffId && m.id === appt.staffId)
    || (!!appt.staffName && (m.name || "").toLowerCase() === appt.staffName.toLowerCase()));
  const staffTz = ((staff as { timezone?: string | null } | undefined)?.timezone) || null;
  const startIso = wallClockToIso(appt.date, appt.start, ctx.zone);
  const endIso = wallClockToIso(appt.date, appt.end, ctx.zone);
  const inStaffZone = staffTz && staffTz !== ctx.zone && startIso
    ? `${friendlyTime(startIso, staffTz)}${endIso ? ` — ${friendlyTime(endIso, staffTz)}` : ""}`
    : null;
  return (
    <TimeZoneNote testid="appt-detail-zone-note">
      {t("appointments.detail.timesBranchLocal", "Times are the branch's local time")} ({zoneLabel(ctx.zone)})
      {inStaffZone ? <> · {appt.staffName || staff?.name}: <b style={{ color: T.n2 }}>{inStaffZone}</b> ({zoneLabel(staffTz as string)})</> : null}
      {ctx.differs ? <> · {t("appointments.detail.deviceZoneDiffers", "your device is on")} {ctx.browserLabel}</> : null}
    </TimeZoneNote>
  );
}

const STATUS_META: Record<ApptStatus, { label: string; fg: string; bg: string }> = {
  booked:        { label: "Booked",       fg: "#4B5563", bg: "#F3F4F6" },
  confirmed:     { label: "Confirmed",    fg: "#1D4ED8", bg: "#EFF4FF" },
  "checked-in":  { label: "Checked in",   fg: "#027D47", bg: "#EAF7F0" },
  served:        { label: "Served",       fg: "#374151", bg: "#EDEFF2" },
  "no-show":     { label: "No-show",      fg: "#B45309", bg: "#FEF4E6" },
  cancelled:     { label: "Cancelled",    fg: "#B91C1C", bg: "#FDECEC" },
  rescheduled:   { label: "Rescheduled",  fg: "#6D28D9", bg: "#F2EEFC" },
};
const CHANNEL_LABEL: Record<string, string> = { inPerson: "In person", phone: "Phone", video: "Video" };
const VIA_LABEL: Record<string, string> = { website: "Website", mobile: "Mobile app", phone: "Phone", email: "Email", staff: "Booked by staff", queue: "From a queue", event: "From an event", journey: "Self-service", import: "Imported" };
const OPEN_STATUSES: ApptStatus[] = ["booked", "confirmed"];
const CURRENCIES = ["CHF", "EUR", "GBP", "USD"];
/** The payment methods a counter actually takes. Cash first — it is the one a
 *  staff member reaches for when someone pays in the boutique. */
const PAY_METHODS: { value: string; label: string }[] = [
  { value: "cash", label: "Cash" },
  { value: "card", label: "Credit card" },
  { value: "debit", label: "Debit card" },
  { value: "online", label: "Online payment" },
  { value: "twint", label: "TWINT" },
  { value: "paypal", label: "PayPal" },
  { value: "applepay", label: "Apple Pay" },
  { value: "banktransfer", label: "Bank transfer" },
  { value: "invoice", label: "Invoice" },
  { value: "giftcard", label: "Gift card" },
  { value: "voucher", label: "Voucher" },
];
const BLOCK_KIND_LABEL: Record<BlockKind, string> = { customer: "Customer", email: "Email address", phone: "Phone number", ip: "IP address" };

const card: CSSProperties = { background: T.white, border: `1px solid ${T.border}`, borderRadius: 12, padding: 20, display: "flex", flexDirection: "column", gap: 16 };
const cardTitle: CSSProperties = { margin: 0, fontSize: 15, fontWeight: 700, color: T.n1, display: "flex", alignItems: "center", gap: 9 };
const metaLabel: CSSProperties = { fontSize: 11, fontWeight: 700, color: T.soft, letterSpacing: "0.05em", textTransform: "uppercase", display: "flex", alignItems: "center", gap: 7 };
const fieldLabel: CSSProperties = { fontSize: 12.5, color: T.n2, fontWeight: 500 };
/** A read value in the shape of the reference's form field — same rhythm, no fake input. */
const fieldBox: CSSProperties = { minHeight: 40, padding: "9px 12px", border: `1px solid ${T.n5}`, borderRadius: 6, background: T.n6, fontSize: 13.5, color: T.n1, display: "flex", alignItems: "center", gap: 8, wordBreak: "break-word" };

function Field({ label, children, hint, span }: { label: string; children: ReactNode; hint?: string; span?: boolean }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 5, gridColumn: span ? "1 / -1" : undefined, minWidth: 0 }}>
      <span style={fieldLabel}>{label}</span>
      <div style={fieldBox}>{children}</div>
      {hint ? <span style={{ fontSize: 11.5, color: T.soft }}>{hint}</span> : null}
    </div>
  );
}

function MetaRow({ label, value, mono }: { label: string; value: ReactNode; mono?: boolean }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
      <span style={{ fontSize: 12, color: T.soft }}>{label}</span>
      <span style={{ fontSize: 13, color: T.n1, fontFamily: mono ? T.fontMono : undefined, wordBreak: "break-word" }}>{value}</span>
    </div>
  );
}

function CopyLink({ label, url }: { label: string; url: string }) {
  const t = useT();
  const copy = () => {
    try { void navigator.clipboard.writeText(url); toast({ tone: "success", title: t("appointments.detail.linkCopied", "Link copied"), subtitle: label }); }
    catch { toast({ tone: "danger", title: t("appointments.detail.copyFailed", "Couldn't copy the link") }); }
  };
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8, minHeight: 30 }}>
      <a href={url} style={{ flex: 1, minWidth: 0, fontSize: 12.5, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{label}</a>
      <Tooltip content={t("common.actions.copy", "Copy")}>
        <button type="button" onClick={copy} aria-label={`${t("common.actions.copy", "Copy")} — ${label}`}
          style={{ width: 28, height: 28, borderRadius: 6, border: "none", background: "transparent", color: T.soft, cursor: "pointer", display: "inline-flex", alignItems: "center", justifyContent: "center" }}>
          <Icon.copy size={14} />
        </button>
      </Tooltip>
    </div>
  );
}

export default function AppointmentDetail() {
  const t = useT();
  const nav = useNav();
  const isMobile = useIsMobile();
  const id = String((nav.params as Record<string, unknown>).id ?? "");
  const canBook = can("appointments.book");
  const canCheckin = can("appointments.checkin");
  const [appt, setAppt] = useState<Appointment | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [resched, setResched] = useState(false);
  const [blocks, setBlocks] = useState<BlockEntry[]>([]);
  const [blockDraft, setBlockDraft] = useState<{ kind: BlockKind; value: string; label: string } | null>(null);
  const [blockReason, setBlockReason] = useState("");
  const [priceDraft, setPriceDraft] = useState<string>("");
  const [currency, setCurrency] = useState("CHF");
  // Collapsed by default (operator, 2026-08-06): the card shows the money line
  // and nothing else until someone actually needs to settle the charge.
  const [payOpen, setPayOpen] = useState(false);

  const load = useCallback(() => {
    if (!id) { setErr("No appointment id in the URL"); return; }
    getAppointment(id).then((a) => { setAppt(a); setErr(null); }).catch((e) => setErr(e instanceof Error ? e.message : String(e)));
  }, [id]);
  useEffect(() => { load(); }, [load]);
  const loadBlocks = useCallback(() => { listBlocks().then((r) => setBlocks(r.entries || [])).catch(() => { /* block list unavailable — the buttons stay, the chips do not lie */ }); }, []);
  useEffect(() => { loadBlocks(); }, [loadBlocks]);
  useEffect(() => {
    if (!appt) return;
    setPriceDraft(appt.price ? String(appt.price.amount) : "");
    setCurrency(appt.price?.currency || "CHF");
  }, [appt?.id, appt?.price?.amount, appt?.price?.currency]); // eslint-disable-line react-hooks/exhaustive-deps

  /* ── Block list ── */
  const blockOf = (kind: BlockKind, value?: string | null) =>
    value ? blocks.find((b) => b.kind === kind && b.value.toLowerCase() === String(value).toLowerCase()) ?? null : null;
  const confirmBlock = async () => {
    if (!blockDraft) return;
    setBusy(true);
    try {
      await addBlock({ kind: blockDraft.kind, value: blockDraft.value, label: blockDraft.label, reason: blockReason.trim() || undefined });
      toast({ tone: "success", title: t("appointments.detail.blocked", "Blocked"), subtitle: `${BLOCK_KIND_LABEL[blockDraft.kind]} · ${blockDraft.value}` });
      setBlockDraft(null); setBlockReason(""); loadBlocks();
    } catch (e) { toast({ tone: "danger", title: t("appointments.detail.blockFailed", "Couldn't block this"), subtitle: e instanceof Error ? e.message : String(e) }); }
    finally { setBusy(false); }
  };
  const unblock = async (entry: BlockEntry) => {
    setBusy(true);
    try { await removeBlock(entry.id); toast({ tone: "success", title: t("appointments.detail.unblocked", "Unblocked"), subtitle: `${BLOCK_KIND_LABEL[entry.kind]} · ${entry.value}` }); loadBlocks(); }
    catch (e) { toast({ tone: "danger", title: t("appointments.detail.unblockFailed", "Couldn't unblock this"), subtitle: e instanceof Error ? e.message : String(e) }); }
    finally { setBusy(false); }
  };

  /* ── Price & payments ── */
  const payments = appt?.payments ?? [];
  const total = appt?.price?.amount ?? 0;
  const paidSum = payments.filter((p) => p.paid).reduce((s, p) => s + (Number(p.amount) || 0), 0);
  const outstanding = Math.max(0, total - paidSum);
  const payState = total <= 0 ? "none" : paidSum <= 0 ? "unpaid" : outstanding > 0 ? "partial" : "paid";
  const money = (n: number) => `${currency} ${n.toFixed(2)}`;
  const savePrice = async (amountStr: string, cur = currency) => {
    if (!appt) return;
    const amount = Number(amountStr.replace(",", "."));
    if (!isFinite(amount) || amount < 0) { toast({ tone: "danger", title: t("appointments.detail.priceInvalid", "Price must be a number of zero or more") }); return; }
    if (appt.price && appt.price.amount === amount && appt.price.currency === cur) return;
    setBusy(true);
    try { setAppt(await setAppointmentPrice(appt.id, { amount, currency: cur })); toast({ tone: "success", title: t("appointments.detail.priceSaved", "Price saved") }); }
    catch (e) { toast({ tone: "danger", title: t("appointments.detail.priceFailed", "Couldn't save the price"), subtitle: e instanceof Error ? e.message : String(e) }); }
    finally { setBusy(false); }
  };
  const payAct = async (fn: () => Promise<Appointment>, msg: string) => {
    setBusy(true);
    try { setAppt(await fn()); toast({ tone: "success", title: msg }); }
    catch (e) { toast({ tone: "danger", title: t("appointments.detail.paymentFailed", "Couldn't update the payment"), subtitle: e instanceof Error ? e.message : String(e) }); }
    finally { setBusy(false); }
  };

  /** Block / unblock control for one identifier. Same shape everywhere it appears. */
  const blockControl = (kind: BlockKind, value?: string | null, label?: string) => {
    if (!value) return null;
    const hit = blockOf(kind, value);
    return hit ? (
      <button type="button" disabled={busy} onClick={() => { void unblock(hit); }} data-testid={`appt-unblock-${kind}`}
        style={{ display: "inline-flex", alignItems: "center", gap: 6, minHeight: 30, padding: "0 10px", borderRadius: 8, border: `1px solid ${T.border}`, background: T.white, color: T.n1, fontFamily: T.font, fontSize: 12, fontWeight: 600, cursor: "pointer" }}>
        <Icon.check size={12} /> {t("appointments.detail.unblock", "Unblock")}
      </button>
    ) : (
      <button type="button" disabled={busy} onClick={() => { setBlockDraft({ kind, value, label: label || value }); setBlockReason(""); }} data-testid={`appt-block-${kind}`}
        style={{ display: "inline-flex", alignItems: "center", gap: 6, minHeight: 30, padding: "0 10px", borderRadius: 8, border: `1px solid ${T.red1}`, background: T.white, color: T.red1, fontFamily: T.font, fontSize: 12, fontWeight: 600, cursor: "pointer" }}>
        <Icon.x size={12} /> {t("appointments.detail.block", "Block")}
      </button>
    );
  };
  const activeBlocks = appt ? ([
    blockOf("customer", appt.customer.name), blockOf("email", appt.customer.email),
    blockOf("phone", appt.customer.phone), blockOf("ip", appt.client?.ip),
  ].filter(Boolean) as BlockEntry[]) : [];

  const act = async (fn: () => Promise<Appointment>, okMsg: string) => {
    setBusy(true);
    try { const next = await fn(); setAppt(next); toast({ tone: "success", title: okMsg }); }
    catch (e) { toast({ tone: "danger", title: t("appointments.book.actionFailed", "Couldn't update the appointment"), subtitle: e instanceof Error ? e.message : String(e) }); }
    finally { setBusy(false); }
  };

  const st = appt ? STATUS_META[appt.status] : null;
  const open = !!appt && OPEN_STATUSES.includes(appt.status);
  const at = appt?.attribution;
  const cl = appt?.client;
  const manageUrl = (action?: string) => {
    const path = routeToPath("appointment.manage", { ref: appt?.ref, ...(action ? { action } : {}) });
    try { return window.location.origin + path; } catch { return path; }
  };

  return (
    <Screen rail="appt-book">
      <PageHeader
        title={appt ? appt.customer.name : t("appointments.detail.appointment", "Appointment")}
        subtitle={appt ? `${appt.service} · ${appt.date} ${appt.start}–${appt.end}` : undefined}
        breadcrumb={
          <span style={{ display: "inline-flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
            <span style={{ cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 4 }} onClick={() => nav.go("appointments")}>
              <Icon.arrow dir="left" size={11} /> {t("appointments.book.appointments", "Appointments")}
            </span>
            <span style={{ color: T.n4 }}>/</span>
            <span style={{ color: T.n2 }}>{appt ? appt.customer.name : t("appointments.detail.appointment", "Appointment")}</span>
          </span>
        }
        actions={st ? <span style={{ padding: "5px 12px", borderRadius: 999, fontSize: 12, fontWeight: 700, color: st.fg, background: st.bg }}>{st.label}</span> : null}
      />
      <div style={{ flex: 1, minHeight: 0, overflow: "auto" }}>
        <LoadState loading={!appt && !err} error={err} onRetry={load} testid="appt-detail"
          noun={t("appointments.detail.appointmentNoun", "appointment")}
          loadingLabel={t("appointments.detail.loading", "Loading the appointment…")}>
          {appt ? (
            <div style={{ padding: isMobile ? "0 16px 32px" : "0 24px 32px", display: "flex", flexDirection: "column", gap: 16 }}>
              {activeBlocks.length ? (
                <div role="status" data-testid="appt-blocked-banner"
                  style={{ display: "flex", gap: 12, alignItems: "flex-start", padding: "14px 16px", borderRadius: 10, background: "#FDECEC", border: "1px solid #F5B5B5" }}>
                  <Icon.warn size={16} style={{ color: "#B91C1C", flexShrink: 0, marginTop: 1 }} />
                  <div style={{ flex: 1, minWidth: 0, fontSize: 13, color: "#8A1F1F", lineHeight: 1.55 }}>
                    <strong>{t("appointments.detail.blockedTitle", "Blocked from booking")}</strong>
                    <div style={{ marginTop: 3 }}>
                      {activeBlocks.map((b) => `${BLOCK_KIND_LABEL[b.kind]}: ${b.value}`).join(" · ")}
                      {activeBlocks.find((b) => b.reason) ? ` — ${activeBlocks.find((b) => b.reason)!.reason}` : ""}
                    </div>
                  </div>
                </div>
              ) : null}

              {/* Action bar — the lifecycle verbs, out in the open (reference: the
                  Umbuchen / Nicht erschienen / Stornieren row). A settled
                  appointment has no verbs left, so the bar is not rendered at all
                  rather than painted empty. */}
              {open || appt.status === "checked-in" ? (
              <div style={{ ...card, padding: 12, flexDirection: "row", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                {open && canBook ? (
                  <Button variant="secondary" disabled={busy} onClick={() => setResched(true)} data-testid="appt-detail-resched">
                    <Icon.refresh size={14} /> {t("appointments.book.reschedule", "Reschedule")}
                  </Button>
                ) : null}
                {open && canCheckin ? (
                  <Button variant="secondary" disabled={busy} onClick={() => { void act(() => markAppointmentNoShow(appt.id), t("appointments.book.noShowToast", "Recorded no-show")); }} data-testid="appt-detail-noshow">
                    <Icon.user size={14} /> {t("appointments.book.noShow", "No-show")}
                  </Button>
                ) : null}
                {open && canBook ? (
                  <button type="button" disabled={busy} data-testid="appt-detail-cancel"
                    onClick={() => { void act(() => cancelAppointment(appt.id), t("appointments.book.cancelledToast", "Appointment cancelled")); }}
                    style={{ display: "inline-flex", alignItems: "center", gap: 7, minHeight: 40, padding: "0 14px", borderRadius: 8, border: `1px solid ${T.red1}`, background: T.white, color: T.red1, fontFamily: T.font, fontSize: 13.5, fontWeight: 600, cursor: busy ? "default" : "pointer", opacity: busy ? 0.6 : 1 }}>
                    <Icon.x size={14} /> {t("appointments.detail.cancelAppointment", "Cancel appointment")}
                  </button>
                ) : null}
                <span style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 10 }}>
                  <span style={{ fontSize: 12, color: T.soft, fontFamily: T.fontMono }}>{appt.ref}</span>
                  {open && canCheckin ? (
                    <Button disabled={busy} onClick={() => { void act(() => checkInAppointment(appt.id), t("appointments.book.checkedInToast", "Checked in")); }} data-testid="appt-detail-checkin">
                      <Icon.check size={14} /> {t("appointments.book.checkIn", "Check in")}
                    </Button>
                  ) : appt.status === "checked-in" && canCheckin ? (
                    <Button disabled={busy} onClick={() => { void act(() => checkInAppointment(appt.id), t("appointments.book.servedToast", "Marked served")); }} data-testid="appt-detail-served">
                      <Icon.check size={14} /> {t("appointments.book.markServed", "Mark served")}
                    </Button>
                  ) : null}
                </span>
              </div>
              ) : null}

              <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "minmax(0, 1fr) 372px", gap: 16, alignItems: "start" }}>
                {/* ── Left: the record ─────────────────────────────────── */}
                <div style={{ display: "flex", flexDirection: "column", gap: 16, minWidth: 0 }}>
                  <section style={card}>
                    <h2 style={cardTitle}><Icon.list size={15} />{t("appointments.detail.basicInformation", "Basic information")}</h2>
                    <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: 14 }}>
                      <Field label={t("appointments.detail.title", "Title")} span><span style={{ fontWeight: 600 }}>{appt.customer.name}</span></Field>
                      <Field label={t("appointments.detail.services", "Services")} span>
                        <span style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "3px 10px", borderRadius: 6, background: T.p4, color: T.p1, fontSize: 12.5, fontWeight: 600 }}>
                          {appt.service} ({appt.durationMin ?? "—"} min)
                        </span>
                      </Field>
                      <Field label={t("appointments.detail.duration", "Duration (minutes)")}>{appt.durationMin ?? "—"}</Field>
                      <Field label={t("appointments.detail.category", "Category")}>{CHANNEL_LABEL[appt.channel] ?? appt.channel}</Field>
                    </div>

                    <div style={{ borderTop: `1px solid ${T.border}`, paddingTop: 16, display: "flex", flexDirection: "column", gap: 12 }}>
                      <h3 style={{ ...cardTitle, fontSize: 13.5 }}>{t("appointments.book.bookingQuestions", "Booking questions")}</h3>
                      {(appt.answers?.length ?? 0) === 0 ? (
                        <p style={{ margin: 0, fontSize: 13, color: T.soft }}>{t("appointments.book.noAnswers", "No booking questions were answered for this appointment.")}</p>
                      ) : (
                        <div style={{ display: "grid", gridTemplateColumns: "1fr", gap: 12 }}>
                          {appt.answers!.map((a, i) => (
                            <Field key={(a.qid || a.question) + i} label={a.question}>{a.answer}</Field>
                          ))}
                        </div>
                      )}
                    </div>
                  </section>

                  <section style={card}>
                    <h2 style={cardTitle}><Icon.users size={15} />{t("appointments.detail.participants", "Participants")}</h2>
                    <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: 14 }}>
                      <Field label={t("appointments.detail.resourceStaff", "Staff")}>
                        <span style={{ width: 24, height: 24, borderRadius: 999, background: T.p4, color: T.p1, fontSize: 10.5, fontWeight: 700, display: "inline-flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                          {(appt.staffName ?? "?").split(" ").map((w) => w[0]).slice(0, 2).join("")}
                        </span>
                        {appt.staffName ?? t("appointments.book.anyStaff", "Any staff")}
                      </Field>
                      <Field label={t("appointments.detail.customer", "Customer")}>
                        {appt.customer.name}{appt.customer.email ? ` (${appt.customer.email})` : ""}
                      </Field>
                      <Field label={t("appointments.detail.where", "Where does the appointment take place?")} span>
                        {CHANNEL_LABEL[appt.channel] ?? appt.channel} · {appt.locationName ?? appt.locationId}
                      </Field>
                      {appt.attendees?.length ? (
                        <Field label={t("appointments.detail.additionalAttendees", "Additional attendees")} span>
                          {appt.attendees.map((x) => x.name).join(", ")}
                        </Field>
                      ) : null}
                      {appt.additionalStaff?.length ? (
                        <Field label={t("appointments.detail.additionalStaff", "Additional staff")} span>
                          {appt.additionalStaff.map((x) => x.name).join(", ")}
                        </Field>
                      ) : null}
                      {appt.resources?.length ? (
                        <Field label={t("appointments.detail.resources", "Resources")} span>
                          {appt.resources.map((x) => x.name).join(", ")}
                        </Field>
                      ) : null}
                    </div>
                  </section>

                  <section style={card}>
                    <h2 style={cardTitle}><Icon.clock size={15} />{t("appointments.detail.dateAndTime", "Date & time")}</h2>
                    <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: 14 }}>
                      <Field label={t("appointments.detail.start", "Start date & time")}><Icon.calendar size={13} />{appt.date} {appt.start}</Field>
                      <Field label={t("appointments.detail.end", "End date & time")}><Icon.calendar size={13} />{appt.date} {appt.end}</Field>
                      <Field label={t("appointments.detail.bookedOn", "Booked on")} span>
                        {appt.bookedAt ? String(appt.bookedAt).replace("T", " ").slice(0, 16) : "—"}
                      </Field>
                    </div>
                    <ApptZoneLine appt={appt} />
                  </section>

                  {/* Price & payment (2026-08-06): the price is SET here and the
                      charge is settled in parts — each payment is its own record
                      with its own method, so "partially paid" is a computed truth,
                      not a flag someone remembered to tick. */}
                  <section style={card}>
                    <h2 style={{ ...cardTitle, justifyContent: "space-between" }}>
                      <span style={{ display: "inline-flex", alignItems: "center", gap: 9 }}><Icon.money size={15} />{t("appointments.detail.priceAndPayment", "Price & payment")}</span>
                      <span style={{ display: "inline-flex", alignItems: "center", gap: 10 }}>
                      <span style={{ padding: "3px 10px", borderRadius: 999, fontSize: 11.5, fontWeight: 700,
                        color: payState === "paid" ? "#027D47" : payState === "partial" ? "#B45309" : payState === "unpaid" ? "#B91C1C" : T.soft,
                        background: payState === "paid" ? "#EAF7F0" : payState === "partial" ? "#FEF4E6" : payState === "unpaid" ? "#FDECEC" : T.n6 }}>
                        {payState === "paid" ? t("appointments.detail.paid", "Paid")
                          : payState === "partial" ? t("appointments.detail.partiallyPaid", "Partially paid")
                          : payState === "unpaid" ? t("appointments.detail.notPaid", "Not paid")
                          : t("appointments.detail.noCharge", "No charge")}
                      </span>
                      <button type="button" onClick={() => setPayOpen((v) => !v)} data-testid="appt-price-toggle" aria-expanded={payOpen}
                        style={{ display: "inline-flex", alignItems: "center", gap: 5, border: "none", background: "transparent", padding: 0, color: T.p1, fontFamily: T.font, fontSize: 12.5, fontWeight: 600, cursor: "pointer" }}>
                        {payOpen ? t("appointments.detail.showLess", "Show less") : t("appointments.detail.showMore", "Show more")}
                        <Icon.chev size={12} dir={payOpen ? "up" : "down"} />
                      </button>
                      </span>
                    </h2>

                    {/* Collapsed: the money line only. */}
                    <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
                      <span style={{ fontSize: 20, fontWeight: 750, color: T.n1 }}>{money(total)}</span>
                      {total > 0 ? (
                        <span style={{ fontSize: 12.5, color: T.soft }}>
                          {t("appointments.detail.totalPaid", "Total paid")} {money(paidSum)}
                          {outstanding > 0 ? " \u00b7 " + t("appointments.detail.outstanding", "Outstanding") + " " + money(outstanding) : ""}
                        </span>
                      ) : (
                        <span style={{ fontSize: 12.5, color: T.soft }}>{t("appointments.detail.setPriceHint", "No price set \u2014 open to set one")}</span>
                      )}
                    </div>

                    {payOpen ? (<>

                    <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "200px 1fr", gap: 14 }}>
                      <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
                        <span style={fieldLabel}>{t("appointments.detail.currency", "Currency")}</span>
                        <Dropdown aria-label={t("appointments.detail.currency", "Currency")} testid="appt-price-currency"
                          value={currency} onChange={(v) => { setCurrency(v); void savePrice(priceDraft || "0", v); }}
                          options={CURRENCIES.map((c) => ({ value: c, label: c }))} />
                      </div>
                      <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
                        <span style={fieldLabel}>{t("appointments.detail.price", "Price")} ({currency})</span>
                        <Input type="number" testid="appt-price-amount" value={priceDraft} disabled={!canBook}
                          onChange={(e) => setPriceDraft(e.target.value)}
                          onBlur={() => { if (priceDraft !== "") void savePrice(priceDraft); }}
                          onKeyDown={(e) => { if (e.key === "Enter") (e.target as HTMLInputElement).blur(); }}
                          placeholder="0.00" />
                      </div>
                    </div>

                    <div style={{ borderRadius: 8, background: T.n6, border: `1px solid ${T.border}`, padding: "12px 14px", display: "flex", flexDirection: "column", gap: 6, fontSize: 13 }}>
                      <div style={{ display: "flex", justifyContent: "space-between" }}>
                        <span style={{ color: T.n2 }}>{t("appointments.detail.total", "Total price")}</span>
                        <span style={{ fontWeight: 700, color: T.n1 }}>{money(total)}</span>
                      </div>
                      <div style={{ display: "flex", justifyContent: "space-between" }}>
                        <span style={{ color: T.n2 }}>{t("appointments.detail.totalPaid", "Total paid")}</span>
                        <span style={{ fontWeight: 700, color: "#027D47" }}>{money(paidSum)}</span>
                      </div>
                      <div style={{ display: "flex", justifyContent: "space-between", borderTop: `1px solid ${T.border}`, paddingTop: 6 }}>
                        <span style={{ color: T.n2 }}>{t("appointments.detail.outstanding", "Outstanding")}</span>
                        <span style={{ fontWeight: 700, color: outstanding > 0 ? "#B45309" : T.n1 }}>{money(outstanding)}</span>
                      </div>
                    </div>

                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, flexWrap: "wrap" }}>
                      <h3 style={{ ...cardTitle, fontSize: 13.5 }}><Icon.list size={14} />{t("appointments.detail.paymentRecords", "Payment records")}</h3>
                      {canBook ? (
                        <Button variant="secondary" size="sm" disabled={busy} data-testid="appt-payment-add"
                          onClick={() => { void payAct(() => addAppointmentPayment(appt.id, { amount: outstanding > 0 ? outstanding : total, date: new Date().toISOString().slice(0, 10), method: "cash", paid: true }), t("appointments.detail.paymentAdded", "Payment recorded")); }}>
                          <Icon.plus size={13} /> {t("appointments.detail.addPayment", "Add payment")}
                        </Button>
                      ) : null}
                    </div>

                    {payments.length === 0 ? (
                      <p style={{ margin: 0, fontSize: 13, color: T.soft }}>{t("appointments.detail.noPayments", "Nothing has been paid against this appointment yet.")}</p>
                    ) : (
                      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                        {payments.map((p) => (
                          <div key={p.id} data-testid={`appt-payment-${p.id}`}
                            style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr 1fr" : "128px 1fr 1fr 1fr 36px", gap: 10, alignItems: "center", padding: "10px 12px", border: `1px solid ${T.border}`, borderRadius: 8 }}>
                            <button type="button" disabled={!canBook || busy}
                              onClick={() => { void payAct(() => updateAppointmentPayment(appt.id, p.id, { paid: !p.paid }), p.paid ? t("appointments.detail.markedPending", "Marked pending") : t("appointments.detail.markedPaid", "Marked paid")); }}
                              style={{ display: "inline-flex", alignItems: "center", gap: 7, minHeight: 30, padding: "0 10px", borderRadius: 999, border: "none", cursor: canBook ? "pointer" : "default", fontFamily: T.font, fontSize: 11.5, fontWeight: 700,
                                background: p.paid ? "#EAF7F0" : T.n6, color: p.paid ? "#027D47" : T.soft }}>
                              <span style={{ width: 26, height: 15, borderRadius: 999, background: p.paid ? "#027D47" : T.n4, position: "relative", flexShrink: 0 }}>
                                <span style={{ position: "absolute", top: 2, left: p.paid ? 13 : 2, width: 11, height: 11, borderRadius: 999, background: T.white }} />
                              </span>
                              {p.paid ? t("appointments.detail.paid", "Paid") : t("appointments.detail.pending", "Pending")}
                            </button>
                            <Input type="number" size="sm" defaultValue={String(p.amount)} disabled={!canBook}
                              onBlur={(e) => { const v = Number(e.target.value); if (isFinite(v) && v > 0 && v !== p.amount) void payAct(() => updateAppointmentPayment(appt.id, p.id, { amount: v }), t("appointments.detail.paymentUpdated", "Payment updated")); }} />
                            <Input type="date" size="sm" defaultValue={p.date} disabled={!canBook}
                              onBlur={(e) => { if (e.target.value && e.target.value !== p.date) void payAct(() => updateAppointmentPayment(appt.id, p.id, { date: e.target.value }), t("appointments.detail.paymentUpdated", "Payment updated")); }} />
                            <Dropdown aria-label={t("appointments.detail.method", "Payment method")} value={p.method} disabled={!canBook}
                              onChange={(v) => { void payAct(() => updateAppointmentPayment(appt.id, p.id, { method: v }), t("appointments.detail.paymentUpdated", "Payment updated")); }}
                              options={PAY_METHODS.map((m) => ({ value: m.value, label: t(`appointments.detail.method.${m.value}`, m.label) }))} />
                            {canBook ? (
                              <Tooltip content={t("appointments.detail.deletePayment", "Delete payment")}>
                                <button type="button" disabled={busy} aria-label={t("appointments.detail.deletePayment", "Delete payment")}
                                  onClick={() => { void payAct(() => deleteAppointmentPayment(appt.id, p.id), t("appointments.detail.paymentDeleted", "Payment deleted")); }}
                                  style={{ width: 32, height: 32, borderRadius: 8, border: "none", background: "transparent", color: T.red1, cursor: "pointer", display: "inline-flex", alignItems: "center", justifyContent: "center", justifySelf: "end" }}>
                                  <Icon.trash size={15} />
                                </button>
                              </Tooltip>
                            ) : null}
                          </div>
                        ))}
                      </div>
                    )}
                    </>) : null}
                  </section>

                  {appt.notes ? (
                    <section style={card}>
                      <h2 style={cardTitle}><Icon.edit size={15} />{t("appointments.book.notes", "Notes")}</h2>
                      <p style={{ margin: 0, fontSize: 13.5, color: T.n1, lineHeight: 1.6, textWrap: "pretty" }}>{appt.notes}</p>
                    </section>
                  ) : null}
                </div>

                {/* ── Right: metadata rail ─────────────────────────────── */}
                <aside style={{ ...card, gap: 20, position: isMobile ? "static" : "sticky", top: 0 }}>
                  <h2 style={cardTitle}><Icon.info size={15} />{t("appointments.detail.metadata", "Appointment metadata")}</h2>

                  <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                    <span style={metaLabel}><Icon.user size={12} />{t("appointments.detail.customer", "Customer")}</span>
                    <div style={{ display: "flex", alignItems: "center", gap: 8, justifyContent: "space-between" }}>
                      <span style={{ fontSize: 13.5, fontWeight: 600, color: T.p1 }}>{appt.customer.name}</span>
                      {blockControl("customer", appt.customer.name)}
                    </div>
                    {appt.customer.email ? (
                      <div style={{ display: "flex", alignItems: "center", gap: 8, justifyContent: "space-between" }}>
                        <a href={`mailto:${appt.customer.email}`} style={{ fontSize: 12.5, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis" }}>{appt.customer.email}</a>
                        {blockControl("email", appt.customer.email)}
                      </div>
                    ) : null}
                    {appt.customer.phone ? (
                      <div style={{ display: "flex", alignItems: "center", gap: 8, justifyContent: "space-between" }}>
                        <span style={{ fontSize: 12.5, color: T.n2 }}>{appt.customer.phone}</span>
                        {blockControl("phone", appt.customer.phone)}
                      </div>
                    ) : null}
                  </div>

                  <div style={{ display: "flex", flexDirection: "column", gap: 12, borderTop: `1px solid ${T.border}`, paddingTop: 16 }}>
                    <MetaRow label={t("appointments.book.reference", "Reference")} value={appt.ref} mono />
                    <MetaRow label={t("appointments.detail.created", "Created")} value={appt.bookedAt ? String(appt.bookedAt).replace("T", " ").slice(0, 16) : "—"} />
                    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                      <span style={metaLabel}><Icon.browser size={12} />{t("appointments.detail.bookingChannel", "Booking channel")}</span>
                      <div style={fieldBox}>{VIA_LABEL[appt.bookedVia || appt.origin || "staff"] ?? "—"}</div>
                      {appt.bookedFrom ? <span style={{ fontSize: 11.5, color: T.soft }}>{appt.bookedFrom}</span> : null}
                    </div>
                  </div>

                  <div style={{ display: "flex", flexDirection: "column", gap: 12, borderTop: `1px solid ${T.border}`, paddingTop: 16 }}>
                    <span style={metaLabel}><Icon.link size={12} />{t("appointments.detail.utmParameters", "UTM parameters")}</span>
                    {at ? (
                      <>
                        <Field label={t("appointments.detail.utmSourceLabel", "Source")} hint={t("appointments.detail.utmSourceHint", "Where the traffic came from (referrer, site name)")}>{at.source || "—"}</Field>
                        <Field label={t("appointments.detail.utmMediumLabel", "Medium")} hint={t("appointments.detail.utmMediumHint", "Marketing medium (CPC, banner, email, social)")}>{at.medium || "—"}</Field>
                        <Field label={t("appointments.detail.utmCampaignLabel", "Campaign")} hint={t("appointments.detail.utmCampaignHint", "Name of the specific campaign or promotion code")}>{at.campaign || "—"}</Field>
                        <Field label={t("appointments.detail.utmTermLabel", "Term")} hint={t("appointments.detail.utmTermHint", "Paid search terms or ad variant")}>{at.term || "—"}</Field>
                        <Field label={t("appointments.detail.utmContentLabel", "Content")} hint={t("appointments.detail.utmContentHint", "Distinguishes ads or links pointing at the same URL")}>{at.content || "—"}</Field>
                        <Field label={t("appointments.detail.utmIdLabel", "ID")} hint={t("appointments.detail.utmIdHint", "A unique campaign id for tracking in analytics")}>{at.id || "—"}</Field>
                      </>
                    ) : (
                      <p style={{ margin: 0, fontSize: 12.5, color: T.soft, lineHeight: 1.55 }}>
                        {t("appointments.book.noAttribution", "No campaign attribution — this booking was taken by a person, not through a self-service link.")}
                      </p>
                    )}
                  </div>

                  {at || cl ? (
                    <div style={{ display: "flex", flexDirection: "column", gap: 12, borderTop: `1px solid ${T.border}`, paddingTop: 16 }}>
                      <span style={metaLabel}><Icon.globe size={12} />{t("appointments.detail.bookingDetails", "Booking details")}</span>
                      {at?.landing ? <MetaRow label={t("appointments.detail.sourceUrl", "Source")} value={<a href={at.landing}>{at.landing}</a>} /> : null}
                      {at?.referrer ? <MetaRow label={t("appointments.book.referrer", "Referrer")} value={at.referrer} /> : null}
                      {cl?.ip ? (
                        <div style={{ display: "flex", alignItems: "flex-end", gap: 8, justifyContent: "space-between" }}>
                          <MetaRow label={t("appointments.detail.ipAddress", "IP address")} value={cl.ip} mono />
                          {blockControl("ip", cl.ip)}
                        </div>
                      ) : null}
                      {cl?.browser ? <MetaRow label={t("appointments.detail.browser", "Browser")} value={cl.browser} /> : null}
                      {cl?.os ? <MetaRow label={t("appointments.detail.os", "Operating system")} value={cl.os} /> : null}
                      {cl?.engine ? <MetaRow label={t("appointments.detail.engine", "Engine")} value={cl.engine} /> : null}
                      {cl?.device || at?.device ? (
                        <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                          <span style={{ fontSize: 12, color: T.soft }}>{t("appointments.book.device", "Device")}</span>
                          <span style={{ alignSelf: "flex-start", padding: "3px 10px", borderRadius: 6, background: T.n6, border: `1px solid ${T.border}`, fontSize: 12, fontWeight: 600, color: T.n1 }}>{cl?.device || at?.device}</span>
                        </div>
                      ) : null}
                      {cl?.userAgent ? (
                        <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                          <span style={{ fontSize: 12, color: T.soft }}>{t("appointments.detail.userAgent", "User agent")}</span>
                          <span style={{ padding: "8px 10px", borderRadius: 6, background: T.n6, border: `1px solid ${T.border}`, fontSize: 11, color: T.n2, fontFamily: T.fontMono, lineHeight: 1.5, wordBreak: "break-all" }}>{cl.userAgent}</span>
                        </div>
                      ) : null}
                    </div>
                  ) : null}

                  <div style={{ display: "flex", flexDirection: "column", gap: 6, borderTop: `1px solid ${T.border}`, paddingTop: 16 }}>
                    <span style={metaLabel}><Icon.external size={12} />{t("appointments.detail.appointmentLinks", "Appointment links")}</span>
                    <span style={{ fontSize: 12, fontWeight: 700, color: T.p1, marginTop: 4 }}>{t("appointments.detail.customerLinks", "Customer links")}</span>
                    <CopyLink label={t("appointments.detail.linkManage", "Manage booking")} url={manageUrl()} />
                    <CopyLink label={t("appointments.detail.linkCancel", "Deep link: cancel")} url={manageUrl("cancel")} />
                    <CopyLink label={t("appointments.detail.linkReschedule", "Deep link: reschedule")} url={manageUrl("reschedule")} />
                  </div>
                </aside>
              </div>
            </div>
          ) : null}
        </LoadState>
      </div>
      {appt && resched ? (
        <RescheduleDrawer appt={appt} onClose={() => setResched(false)}
          onDone={(a) => { setResched(false); setAppt(a); toast({ tone: "success", title: t("appointments.book.rescheduledToast", "Rescheduled"), subtitle: `${a.date} · ${a.start}` }); }} />
      ) : null}
      {blockDraft ? (
        <Modal tone="danger" testid="appt-block-modal" icon={<Icon.warn size={16} />}
          title={`${t("appointments.detail.block", "Block")} · ${BLOCK_KIND_LABEL[blockDraft.kind]}`}
          sub={t("appointments.detail.blockSub", "New bookings from this identifier are refused until it is unblocked. Existing appointments are left alone.")}
          onClose={() => setBlockDraft(null)}
          footer={<>
            <Button variant="secondary" onClick={() => setBlockDraft(null)}>{t("common.actions.cancel", "Cancel")}</Button>
            <Button disabled={busy} onClick={() => { void confirmBlock(); }} data-testid="appt-block-confirm">{t("appointments.detail.blockConfirm", "Block")}</Button>
          </>}>
          <div style={fieldBox}>{blockDraft.value}</div>
          <Input label={t("appointments.detail.blockReason", "Reason (optional)")} value={blockReason}
            onChange={(e) => setBlockReason(e.target.value)} testid="appt-block-reason"
            placeholder={t("appointments.detail.blockReasonHint", "Recorded on the block list, e.g. repeated no-shows")} />
        </Modal>
      ) : null}
    </Screen>
  );
}
